import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as yup from "yup";
import { HashLoader } from "react-spinners";
import PasswordCheck from "../../components/PasswordCheck";
import useCompleteOnboardingProfile from "../../hooks/useCompleteOnboardingProfile";
import SignUpNavbar from "../../components/SignUpNavbar";
import { useAuth } from "../../context/AuthContext";

/** Matches Core CompleteProfileDto (Visaro Core). */
const passwordStrength = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\w\W]{8,}$/;

const schema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .matches(
      passwordStrength,
      "Use at least 8 characters with uppercase, number, and special character"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

const CreatePassword = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const navigate = useNavigate();
  const { refetch } = useAuth();

  const [passwordIsVisible, setPasswordIsVisible] = useState(false);

  const {
    completeProfile,
    completingProfile,
    completeProfileError,
    isCompleteProfileError,
  } = useCompleteOnboardingProfile();

  return (
    <div>
      {completingProfile && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        initialValues={{
          password: "",
          confirmPassword: "",
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          completeProfile(
            { password: values.password },
            {
              onError(error) {
                console.error(error);
              },
              async onSuccess() {
                // Password is the last data step. Refresh the profile, then show
                // the welcome screen. The profile photo is a dashboard setup task.
                await refetch();
                navigate("/sign-up/success", { replace: true });
              },
            }
          );
        }}
      >
        {({ isValid, dirty, values, setTouched, handleChange, handleBlur }) => {
          return (
            <div>
              <SignUpNavbar />
              <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
                <div className="flex flex-col justify-center items-center gap-2">
                  <div className="text-4xl md:text-3xl lg:text-5xl max-w-3xl font-bold text-center">
                    Secure Your Account
                  </div>
                  <div className="text-gray-500 text-center max-w-lg">
                    You can now set up a new password. Enter the following to
                    continue.
                  </div>
                </div>
                <Form className="w-full flex-col gap-5 flex items-center justify-center text-base">
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="password"
                      className="text-sm font-bold text-gray-700"
                    >
                      Password*
                    </label>
                    <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                      <Field
                        onFocus={() =>
                          setTouched(
                            {
                              password: true,
                            },
                            false
                          )
                        }
                        autoComplete="off"
                        type={passwordIsVisible ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Create a password"
                        className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                      />
                      <div
                        onClick={() => setPasswordIsVisible((prev) => !prev)}
                        className="absolute right-0 m-4 cursor-pointer text-sm"
                      >
                        {passwordIsVisible ? "Hide" : "Show"}
                      </div>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="text-sm font-bold text-gray-700"
                    >
                      Confirm Password*
                    </label>
                    <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                      <Field
                        onFocus={() =>
                          setTouched(
                            {
                              password: true,
                            },
                            false
                          )
                        }
                        autoComplete="off"
                        type={passwordIsVisible ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                      />
                      <div
                        onClick={() => setPasswordIsVisible((prev) => !prev)}
                        className="absolute right-0 m-4 cursor-pointer text-sm"
                      >
                        {passwordIsVisible ? "Hide" : "Show"}
                      </div>
                    </div>
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500"
                    />
                    <PasswordCheck password={values.password} />
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    {isCompleteProfileError && (
                      <div className="text-red-500 text-left flex">
                        {completeProfileError?.message}
                      </div>
                    )}
                    <button
                      disabled={!isValid || !dirty}
                      type="submit"
                      className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                    >
                      {completingProfile ? "Loading" : "Continue"}
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreatePassword;
